import { useState, useEffect } from 'react';

/**
 * useMediaQuery — Reactive CSS media query hook
 * Returns true when the query matches, false otherwise.
 * Updates automatically on window resize / orientation change.
 *
 * @param {string} query - CSS media query string, e.g. '(max-width: 767px)'
 * @returns {boolean}
 */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const media = window.matchMedia(query);

    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    const listener = (e) => setMatches(e.matches);

    if (media.addEventListener) {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    } else {
      // Fallback for older browsers
      media.addListener(listener);
      return () => media.removeListener(listener);
    }
  }, [query, matches]);

  return matches;
};

// ── Preset breakpoint hooks (Tailwind defaults) ──

/** Mobile: < 768px */
export const useIsMobile = () => useMediaQuery('(max-width: 767px)');

/** Tablet: 768px – 1023px */
export const useIsTablet = () => useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

/** Desktop: >= 1024px */
export const useIsDesktop = () => useMediaQuery('(min-width: 1024px)');

/** Wide: >= 1536px */
export const useIsWide = () => useMediaQuery('(min-width: 1536px)');
